import { createContext, useContext, useState, useEffect } from 'react'

type NavigationSourceType =
  | 'campaign'
  | 'category'
  | 'discount'
  | 'warehouse'
  | 'topproducts'
  | 'search'
  | 'manufacturer'
  | 'wishlist'

export type NavigationSource = {
  from: NavigationSourceType
  name?: string
  slug?: string
}

type NavigationSourceContextType = {
  navigationSource: NavigationSource | null
  setNavigationSource: (source: NavigationSource | null) => void
  clearNavigationSource: () => void
}

const NavigationSourceContext =
  createContext<NavigationSourceContextType | undefined>(undefined)

export const useNavigationSource = (): NavigationSourceContextType => {
  const context = useContext(NavigationSourceContext)
  if (!context) {
    throw new Error(
      'useNavigationSource must be used within NavigationSourceProvider'
    )
  }
  return context
}

const STORAGE_KEY = 'navigationSource'

export const NavigationSourceProvider: React.FC = ({ children }) => {
  const [navigationSource, setNavigationSourceState] =
    useState<NavigationSource | null>(null)

  // Načítanie zo sessionStorage pri mount
  useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY)
    if (saved) {
      setNavigationSourceState(JSON.parse(saved))
    }
  }, [])

  const setNavigationSource = (source: NavigationSource | null) => {
    setNavigationSourceState(source)
    if (source) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(source))
    } else {
      sessionStorage.removeItem(STORAGE_KEY)
    }
  }

  const clearNavigationSource = () => {
    setNavigationSource(null)
  }

  const value = {
    navigationSource,
    setNavigationSource,
    clearNavigationSource
  }

  return (
    <NavigationSourceContext.Provider value={value}>
      {children}
    </NavigationSourceContext.Provider>
  )
}
